import type { SignupApiError } from './api';
import type { ConnectReturn } from './storage';
import type {
    Account,
    AccountView,
    CodeOption,
    Credentials,
    Derived,
    Draft,
    LogoChoice,
    PreviewState,
    Restech,
    SignupPreview,
} from './types';

// The signup app's state machine (plan §6, §16): boot (preview) → landing (state-specific) → claim →
// connect → pick account (several) → confirm → verify e-mail (unbound links) → create account → done
// (redirect to the dashboard's sign-in), or `returning` when only closed tenants hold the company (create's
// `409 returning_client`: ops finish the setup with the client). "Continue on another device" releases the claim from any
// wizard step; a lost session (released elsewhere, revoked, expired, used) goes back to boot, which
// previews again and lands on whatever the link now is.
//
// Credentials live in this state only (memory): never in storage, never sent anywhere but the
// booking-system calls, wiped by a release or a lost session. A booking system connected by OAuth needs none
// here: its credentials stay on the server (`credentials: {}`).

export type Step =
    | 'boot'
    | 'no_code'
    | 'failed'
    | 'landing'
    | 'connect'
    | 'pick'
    | 'confirm'
    | 'verify'
    | 'create'
    | 'done'
    | 'returning'
    | 'released';

export const WIZARD_STEPS: Step[] = ['connect', 'pick', 'confirm', 'verify', 'create'];

/** The confirm card's choices. */
export interface Details {
    name: string;
    code: string;
    /** The code was typed (an older backend's editable field, or "Other"), not picked from the code options. */
    code_typed?: boolean;
    lang_enabled: string[];
    default_lang: string;
    logo: LogoChoice;
    /** "Do you want your own branded mobile app?" (backend has-app plan §4). */
    has_app?: boolean;
    /** An existing Branch.io pair (optional, with an app only; both or neither). In memory only, like the
     *  credentials: never stored, sent only with create. */
    branchio_key?: string;
    branchio_secret?: string;
    /** "Also create a sandbox" (asked only while the booking-system list says `sandbox_offered`). */
    sandbox?: boolean;
}

/** A refusal carried back to the screen that can fix it (e.g. create → confirm for `code_taken`). */
export interface Flash {
    message: string;
    field?: string;
}

export interface FlowState {
    step: Step;
    /** Bumped by every boot, so "Try again" previews again even from a failed boot. */
    bootSeq: number;
    preview: SignupPreview | null;
    failure: SignupApiError | null;
    /** Why the page went back to the landing (a lost session). */
    notice: string | null;
    claimed: boolean;
    /** What this browser chose before (`holder.draft` of a `yours` preview), to preselect it. */
    resume: Draft | null;
    restech: Restech | null;
    credentials: Credentials;
    accounts: Account[] | null;
    account: string | null;
    view: AccountView | null;
    details: Details | null;
    verifiedEmail: string | null;
    flash: Flash | null;
    signInUrl: string | null;
    /** Create's `409 returning_client` words (the server's `detail`), shown on the `returning` end screen. */
    handover: string | null;
    /** What "Connect with <booking system>" came back with (read from the URL at load: candidates, acted on only for
     *  a restech code), until Connect shows it. It outlives boot and claim: the page comes back through the landing. */
    connectReturns: ConnectReturn[];
    /** Connect goes on with a connected account by itself: true when it is reached by the claim, false once the
     *  client moved on or came back to it ("Back", a refusal sent there). */
    autoConnect: boolean;
    /** `GET restechs` said sandboxes are offered here: Confirm asks for one, create sends the answer. */
    sandboxOffered: boolean;
}

export type FlowAction =
    | { type: 'boot'; notice?: string | null }
    | { type: 'previewed'; preview: SignupPreview }
    | { type: 'preview_failed'; failure: SignupApiError }
    | { type: 'claim_refused'; state: PreviewState; claimed_since?: string | null; sign_in_url?: string | null }
    | { type: 'claimed' }
    | { type: 'restechs_listed'; sandboxOffered: boolean }
    | { type: 'accounts_listed'; restech: Restech; credentials: Credentials; accounts: Account[] }
    | { type: 'account_viewed'; restech: Restech; credentials: Credentials; accounts: Account[]; account: string; view: AccountView }
    | { type: 'confirmed'; details: Details }
    | { type: 'verified'; email: string }
    | { type: 'go'; step: Step; flash?: Flash | null; alreadyOnboarded?: boolean; unverify?: boolean; codeOptions?: CodeOption[] }
    | { type: 'created'; signInUrl: string }
    | { type: 'handed_over'; message: string }
    | { type: 'released' }
    | { type: 'session_lost'; notice: string }
    | { type: 'connect_return_seen' };

const WIPED = {
    claimed: false,
    resume: null,
    restech: null,
    credentials: {},
    accounts: null,
    account: null,
    view: null,
    details: null,
    verifiedEmail: null,
    flash: null,
    autoConnect: false,
    handover: null,
};

export function initialFlow(hasCode: boolean, connectReturns: ConnectReturn[] = []): FlowState {
    return {
        step: hasCode ? 'boot' : 'no_code',
        bootSeq: 0,
        preview: null,
        failure: null,
        notice: null,
        signInUrl: null,
        connectReturns: hasCode ? connectReturns : [],
        sandboxOffered: false,
        ...WIPED,
    };
}

export function flowReducer(state: FlowState, action: FlowAction): FlowState {
    switch (action.type) {
        case 'boot':
            return { ...state, ...WIPED, step: 'boot', bootSeq: state.bootSeq + 1, failure: null, notice: action.notice ?? null };
        case 'previewed':
            return { ...state, step: 'landing', preview: action.preview, failure: null };
        case 'preview_failed':
            return { ...state, step: 'failed', failure: action.failure };
        case 'claim_refused': {
            if (!state.preview) {
                return state;
            }
            const preview: SignupPreview = { ...state.preview, state: action.state };
            delete preview.holder;
            if (action.claimed_since) {
                preview.claimed_since = action.claimed_since;
            }
            if (action.sign_in_url) {
                preview.sign_in_url = action.sign_in_url;
            }
            // A refused claim (the resumed one after a connect return too) ends what that return was about.
            return { ...state, ...WIPED, step: 'landing', preview, notice: null, connectReturns: [] };
        }
        case 'claimed': {
            const holder = state.preview?.holder;
            return {
                ...state,
                ...WIPED,
                step: 'connect',
                claimed: true,
                autoConnect: true,
                notice: null,
                resume: holder?.draft ?? null,
                verifiedEmail: holder?.verified_email ?? null,
            };
        }
        case 'restechs_listed':
            return state.sandboxOffered === action.sandboxOffered ? state : { ...state, sandboxOffered: action.sandboxOffered };
        case 'accounts_listed':
            return {
                ...state,
                step: 'pick',
                restech: action.restech,
                credentials: action.credentials,
                accounts: action.accounts,
                flash: null,
                autoConnect: false,
            };
        case 'account_viewed': {
            const sameAccount = state.account === action.account && state.restech?.code === action.restech.code;
            return {
                ...state,
                step: 'confirm',
                restech: action.restech,
                credentials: action.credentials,
                accounts: action.accounts,
                account: action.account,
                view: action.view,
                details: sameAccount ? state.details : null,
                flash: null,
                autoConnect: false,
            };
        }
        case 'confirmed': {
            const bound = !!state.preview?.invite.bound;
            return { ...state, details: action.details, step: bound || state.verifiedEmail ? 'create' : 'verify', flash: null };
        }
        case 'verified':
            return { ...state, verifiedEmail: action.email, step: 'create', flash: null };
        case 'go': {
            // A create refused as already_onboarded: the duplicate is not allowed (any more), whatever `account` said.
            let view = action.alreadyOnboarded && state.view
                ? { ...state.view, already_onboarded: true, duplicates_allowed: false }
                : state.view;
            // A create refused as code_taken brought the code options afresh: the confirm card offers those.
            if (action.codeOptions && view) {
                view = { ...view, derived: withCodeOptions(view.derived, action.codeOptions) };
            }
            return {
                ...state,
                step: action.step,
                flash: action.flash ?? null,
                view,
                verifiedEmail: action.unverify ? null : state.verifiedEmail,
                autoConnect: false,
            };
        }
        case 'created':
            return { ...state, ...WIPED, step: 'done', signInUrl: action.signInUrl };
        case 'handed_over':
            // Only closed tenants hold the company: the end of this flow. Nothing to retry, the credentials wiped, and
            // nothing about the old account shown -- just the server's words.
            return { ...state, ...WIPED, step: 'returning', handover: action.message };
        case 'released':
            return { ...state, ...WIPED, step: 'released', connectReturns: [] };
        case 'session_lost':
            return { ...state, ...WIPED, step: 'boot', bootSeq: state.bootSeq + 1, failure: null, notice: action.notice };
        case 'connect_return_seen':
            return { ...state, connectReturns: [] };
        default:
            return state;
    }
}

/** The derived identity with fresh code options (`409 code_taken`): the default pick is the first available one, as
 *  the account step answers it; the original when none is. */
export function withCodeOptions(derived: Derived, options: CodeOption[]): Derived {
    const pick = options.find((o) => o.available);
    return { ...derived, code_options: options, code: (pick || options[0] || derived).code, code_available: !!pick };
}

/** The step "Back" returns to from the confirm card. */
export function backFromConfirm(state: FlowState): Step {
    return state.accounts && state.accounts.length > 1 ? 'pick' : 'connect';
}
